import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { beGetSalonTaxesAndFees } from 'actions/salon/beGetSalonTaxesAndFees.action';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import LINK from 'constants/link.const';
import USER_ROLE from 'constants/role';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { selectAccessPermissionType } from 'selectors/access.selectors';
import linkTo from 'utils/links/linkTo.util';

import CN from './TaxesBanner.module.scss';

export const TaxesBanner = ({ location, isBannerHidden = false }) => {
  const [isClosed, setIsClosed] = useState(false);
  const salonId = useCurrentSalonId();
  const { qj: taxesAndFees, isLoading } = usePrepareReadOnly(beGetSalonTaxesAndFees);
  const permissionType = useSelector(selectAccessPermissionType);
  const { isSalonInUS } = useGilt();
  const { t } = useTranslation();

  const taxes = useMemo(() => taxesAndFees?.taxes ?? [], [taxesAndFees]);

  const isPreviouslyHidden = sessionStorage.getItem(`banner-${salonId}-${location}`) === 'true';
  const isManager = permissionType === USER_ROLE.manager;
  const hasInvalidTaxes = taxes.length > 0;

  const isBannerInvalid = useMemo(() => isPreviouslyHidden || !isSalonInUS || isLoading || hasInvalidTaxes || !isManager, [isPreviouslyHidden, isSalonInUS, isLoading, isManager, hasInvalidTaxes]);

  const isVisible = !isBannerInvalid && !isClosed && !isBannerHidden;

  if (!isVisible) return null;

  const handleClose = () => {
    sessionStorage.setItem(`banner-${salonId}-${location}`, 'true');
    setIsClosed(true);
  };

  const navLink = linkTo({ pattern: LINK.taxesAndFees, params: { salonId }, fault: '#' });

  return (
    <div data-bem="TaxesBanner" className={CN.component}>
      <AlertPanel variant="alert" size="sm">
        <div className={CN.container}>
          <div className={CN.leftContainer}>
            <Icon variant="alert" />
            <span className={CN.title}>{t('composite.taxBanner.title')}</span>
          </div>
          <div className={CN.rightContainer}>
            <NavLink className={CN.menuItem} activeClassName={CN.selected} to={navLink}>
              {t('composite.taxBanner.link')}
            </NavLink>
            <Button data-bem="TaxesBanner__closeButton" variant="empty" aria-label="Close banner" onClick={handleClose}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </div>
      </AlertPanel>
    </div>
  );
};
